import { createEntityAdapter, EntityAdapter } from '@ngrx/entity';
import { Action, createReducer, on } from '@ngrx/store';

import { Cycle, CycleModuleState } from '../../models/cycle.model';
import * as cycleActions from '../actions/cycle.actions';

function selectIdNumber(c: Cycle): number {
  return c.id;
}

function sortByCycleNumber(a: Cycle, b: Cycle): number {
  return b.cycleNumber - a.cycleNumber;
}

export const adapter: EntityAdapter<Cycle> = createEntityAdapter<Cycle>({
  selectId: selectIdNumber,
  sortComparer: sortByCycleNumber,
});

export const initialState = <CycleModuleState>{
  cycles: adapter.getInitialState([]),
  propagationCompanies: [],
  hatcheries: [],
  selectedCycleId: 0,
  isDetailedOverview: false,
  routeTransitionBalance: null,
  isLoaded: false,
};

const cycleReducer = createReducer(
  initialState,
  on(cycleActions.clearCycleState, (state) => ({
    ...state,
    selectedCycleId: 0,
    selectedOrderId: undefined as number,
    selectedArrivalId: undefined as number,
    selectedSectionId: undefined as number,
    selectedMovementId: undefined as number,
    selectedDepartureId: undefined as number,
    isLoaded: false,
    deliveries: undefined as unknown,
    cycles: adapter.removeAll(state.cycles),
  })),
  on(cycleActions.loadCyclesSuccess, (state, { cycles }) => ({
    ...state,
    selectedCycleId: 0,
    isLoaded: true,
    cycles: adapter.setAll(cycles, state.cycles),
  })),
  on(cycleActions.upsertCycleSuccess, (state, { cycle }) => ({
    ...state,
    selectedProduct: undefined as unknown,
    cycles: adapter.upsertOne(cycle, state.cycles),
    selectedCycleId: cycle.id,
  })),
  on(cycleActions.deleteCycleSuccess, (state, { cycle }) => ({
    ...state,
    selectedProduct: undefined as unknown,
    selectedCycleId: 0,
    cycles: adapter.removeOne(cycle.id, state.cycles),
  })),
  on(cycleActions.upsertCycleGeneral, (state, { cycle }) => ({
    ...state,
    cycles: adapter.upsertOne(cycle, state.cycles),
  })),

  /** ORDERS */
  on(cycleActions.addOrder, (state, { cycleId, order }) => ({
    ...state,
    cycles: adapter.updateOne(
      {
        id: cycleId,
        changes: {
          orders: [...(state.cycles.entities[cycleId].orders || []), order],
        },
      },
      state.cycles,
    ),
  })),
  on(cycleActions.editOrder, (state, { cycleId, order }) => ({
    ...state,
    cycles: adapter.updateOne(
      {
        id: cycleId,
        changes: {
          orders: [
            ...state.cycles.entities[cycleId].orders.filter((c) => c.id !== order.id),
            order,
          ],
        },
      },
      state.cycles,
    ),
  })),
  on(cycleActions.deleteOrder, (state, { cycleId, id }) => ({
    ...state,
    cycles: adapter.updateOne(
      {
        id: cycleId,
        changes: {
          orders: [...state.cycles.entities[cycleId].orders.filter((c) => c.id !== id)],
        },
      },
      state.cycles,
    ),
  })),
  on(cycleActions.setSelectedOrderId, (state, { id }) => ({
    ...state,
    selectedOrderId: id,
  })),
  on(cycleActions.addSection, (state, { cycleId, section }) => ({
    ...state,
    cycles: adapter.updateOne(
      {
        id: cycleId,
        changes: {
          sections: [...(state.cycles.entities[cycleId].sections || []), section],
        },
      },
      state.cycles,
    ),
  })),

  /** SECTIONS */
  on(cycleActions.editSection, (state, { cycleId, section }) => ({
    ...state,
    cycles: adapter.updateOne(
      {
        id: cycleId,
        changes: {
          sections: [
            ...state.cycles.entities[cycleId].sections.filter((c) => c.id !== section.id),
            section,
          ],
        },
      },
      state.cycles,
    ),
  })),
  on(cycleActions.deleteSection, (state, { cycleId, id }) => ({
    ...state,
    cycles: adapter.updateOne(
      {
        id: cycleId,
        changes: {
          sections: [...state.cycles.entities[cycleId].sections.filter((c) => c.id !== id)],
        },
      },
      state.cycles,
    ),
  })),
  on(cycleActions.setSelectedSectionId, (state, { id }) => ({
    ...state,
    selectedSectionId: id,
  })),
  on(cycleActions.loadPropagationCompaniesSuccess, (state, { propagationCompanies }) => ({
    ...state,
    propagationCompanies,
  })),

  /** ARRIVALS */
  on(cycleActions.addArrival, (state, { cycleId, arrival }) => ({
    ...state,
    cycles: adapter.updateOne(
      {
        id: cycleId,
        changes: {
          arrivals: [...(state.cycles.entities[cycleId].arrivals || []), arrival],
        },
      },
      state.cycles,
    ),
  })),
  on(cycleActions.editArrival, (state, { cycleId, arrival }) => ({
    ...state,
    cycles: adapter.updateOne(
      {
        id: cycleId,
        changes: {
          arrivals: [
            ...state.cycles.entities[cycleId].arrivals.filter((c) => c.id !== arrival.id),
            arrival,
          ],
        },
      },
      state.cycles,
    ),
  })),
  on(cycleActions.deleteArrival, (state, { cycleId, id }) => ({
    ...state,
    cycles: adapter.updateOne(
      {
        id: cycleId,
        changes: {
          arrivals: [...state.cycles.entities[cycleId].arrivals.filter((c) => c.id !== id)],
        },
      },
      state.cycles,
    ),
  })),
  on(cycleActions.setSelectedArrivalId, (state, { id }) => ({
    ...state,
    selectedArrivalId: id,
  })),
  /** MOVEMENTS */
  on(cycleActions.addMovement, (state, { cycleId, movement }) => ({
    ...state,
    cycles: adapter.updateOne(
      {
        id: cycleId,
        changes: {
          movements: [...(state.cycles.entities[cycleId].movements || []), movement],
        },
      },
      state.cycles,
    ),
  })),
  on(cycleActions.editMovement, (state, { cycleId, movement }) => ({
    ...state,
    cycles: adapter.updateOne(
      {
        id: cycleId,
        changes: {
          movements: [
            ...state.cycles.entities[cycleId].movements.filter((c) => c.id !== movement.id),
            movement,
          ],
        },
      },
      state.cycles,
    ),
  })),
  on(cycleActions.deleteMovement, (state, { cycleId, id }) => ({
    ...state,
    cycles: adapter.updateOne(
      {
        id: cycleId,
        changes: {
          movements: [...state.cycles.entities[cycleId].movements.filter((c) => c.id !== id)],
        },
      },
      state.cycles,
    ),
  })),
  on(cycleActions.setSelectedMovementId, (state, { id }) => ({
    ...state,
    selectedMovementId: id,
  })),
  on(cycleActions.loadHatcheriesSuccess, (state, { hatcheries }) => ({
    ...state,
    hatcheries,
  })),

  /** DEPARTURES */
  on(cycleActions.addDeparture, (state, { cycleId, departure }) => ({
    ...state,
    cycles: adapter.updateOne(
      {
        id: cycleId,
        changes: {
          departures: [...(state.cycles.entities[cycleId].departures || []), departure],
        },
      },
      state.cycles,
    ),
  })),
  on(cycleActions.editDeparture, (state, { cycleId, departure }) => ({
    ...state,
    cycles: adapter.updateOne(
      {
        id: cycleId,
        changes: {
          departures: [
            ...state.cycles.entities[cycleId].departures.filter((c) => c.id !== departure.id),
            departure,
          ],
        },
      },
      state.cycles,
    ),
  })),
  on(cycleActions.deleteDeparture, (state, { cycleId, id }) => ({
    ...state,
    cycles: adapter.updateOne(
      {
        id: cycleId,
        changes: {
          departures: [...state.cycles.entities[cycleId].departures.filter((c) => c.id !== id)],
        },
      },
      state.cycles,
    ),
  })),
  on(cycleActions.setSelectedDepartureId, (state, { id }) => ({
    ...state,
    selectedDepartureId: id,
  })),
);

export function reducer(state: CycleModuleState | undefined, action: Action) {
  return cycleReducer(state, action);
}
